import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from './SEO';
import './LandingPage.css';

export interface PlanOption {
  key: string;
  priceId: string;
  label: string;
}

const PLANS: PlanOption[] = [
  {
    key: 'free',
    priceId: '',
    label: 'Grátis',
  },
  {
    key: 'basico',
    priceId: process.env.REACT_APP_PRICE_BASICO || '',
    label: 'Básico',
  },
  {
    key: 'pro',
    priceId: process.env.REACT_APP_PRICE_PRO || '',
    label: 'Pro',
  },
  {
    key: 'intensivo',
    priceId: process.env.REACT_APP_PRICE_INTENSIVO || '',
    label: 'Intensivo',
  },
];

export default function LandingPage() {
  const navigate = useNavigate();

  const onGetStarted = () => navigate('/login');
  const onSelectPlan = (plan: PlanOption) => navigate('/register', { state: { selectedPlan: plan } });
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
  };
  const scrollToFeatures = () => {
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
  };

  const [activeSection, setActiveSection] = useState('how-it-works');

  useEffect(() => {
    const sections = ['how-it-works', 'features', 'pricing'];
    const sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3, rootMargin: '-80px 0px 0px 0px' }
    );
    sections.forEach(id => {
      const el = document.getElementById(id);
      if (el) sectionObserver.observe(el);
    });
    return () => sectionObserver.disconnect();
  }, []);

  useEffect(() => {
    const elements = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="bg-background text-on-background font-body antialiased">
      <SEO
        title="Simulador de Diagnóstico Médico com IA"
        description="Pratique raciocínio clínico com pacientes virtuais impulsionados por IA. Gere casos clínicos únicos e imprevisíveis em segundos."
        path="/"
      />
      {/* Top Navigation Bar */}
      <nav className="fixed top-0 w-full z-50 bg-[#0b1326]/80 backdrop-blur-xl shadow-2xl shadow-sky-900/20">
        <div className="flex items-center justify-between px-8 py-4 max-w-7xl mx-auto font-['Plus_Jakarta_Sans'] text-sm tracking-tight">
          <div className="text-2xl font-bold tracking-tighter text-sky-400">MedPuzzle</div>
          <div className="hidden md:flex items-center space-x-8">
            <button className={`pb-1 transition-colors ${activeSection === 'how-it-works' ? 'text-sky-400 font-semibold border-b-2 border-sky-400' : 'text-slate-400 hover:text-sky-300'}`} onClick={scrollToFeatures}>Como Funciona</button>
            <button className={`pb-1 transition-colors ${activeSection === 'features' ? 'text-sky-400 font-semibold border-b-2 border-sky-400' : 'text-slate-400 hover:text-sky-300'}`} onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}>Especialidades</button>
            <button className={`pb-1 transition-colors ${activeSection === 'pricing' ? 'text-sky-400 font-semibold border-b-2 border-sky-400' : 'text-slate-400 hover:text-sky-300'}`} onClick={scrollToPricing}>Preços</button>
          </div>
          <div className="flex items-center space-x-6">
            <button className="text-slate-400 hover:text-sky-300 transition-colors font-medium active:scale-95 duration-200" onClick={onGetStarted}>Login</button>
            <button className="bg-gradient-to-br from-primary to-primary-container text-on-primary-container px-6 py-2.5 rounded-full font-bold hover:opacity-80 active:scale-95 transition-all duration-300" onClick={() => onSelectPlan(PLANS[0])}>Iniciar Caso Grátis</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative pt-40 pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,_rgba(14,165,233,0.15),transparent_50%)]"></div>
        <div className="max-w-[1440px] mx-auto px-8 grid lg:grid-cols-2 gap-20 items-center relative z-10">
          <div className="space-y-8">
            <span className="inline-block px-4 py-1.5 rounded-full bg-surface-container-highest text-primary text-sm font-label font-semibold tracking-wider uppercase">Nova Geração de Simulação Médica</span>
            <h1 className="font-headline text-6xl md:text-7xl font-extrabold tracking-tight leading-[1.1]">
              Gere Casos Clínicos <br/><span className="text-primary-fixed-dim">Únicos com IA</span> <br/>em Segundos
            </h1>
            <p className="text-on-surface-variant text-xl leading-relaxed max-w-xl">
              Diferente de bibliotecas estáticas, nossa IA gera dinamicamente cenários clínicos infinitos e imprevisíveis. Treine com pacientes que nunca foram vistos antes, adaptados ao seu nível.
            </p>
            <div className="flex items-center gap-6 pt-4">
              <button className="bg-gradient-to-br from-primary to-primary-container text-on-primary-container px-10 py-4 rounded-full font-headline font-bold text-lg hover:opacity-90 active:scale-95 transition-all shadow-[0_0_40px_rgba(14,165,233,0.4)]" onClick={() => onSelectPlan(PLANS[0])}>Gerar Meu Primeiro Caso</button>
              <button className="flex items-center gap-3 text-on-surface hover:text-primary transition-colors font-semibold group" onClick={scrollToFeatures}>
                <span className="material-symbols-outlined p-2 rounded-full bg-surface-container-high group-hover:bg-surface-variant">play_arrow</span>
                Ver Como Funciona
              </button>
            </div>
          </div>
          <div className="relative hidden lg:block">
            <div className="absolute -inset-10 bg-primary/20 blur-[100px] rounded-full"></div>
            <div className="relative glass-card border border-outline-variant/30 rounded-2xl overflow-hidden shadow-2xl p-12 aspect-square flex flex-col justify-center items-center text-center">
              <div className="mb-8 p-6 bg-primary/10 rounded-full animate-pulse">
                <span className="material-symbols-outlined text-primary text-8xl" style={{ fontVariationSettings: "'FILL' 1" }}>psychology</span>
              </div>
              <h3 className="font-headline text-3xl font-bold mb-4">Motor Generativo Ativo</h3>
              <p className="text-on-surface-variant max-w-xs mx-auto">Sintetizando histórico, exames laboratoriais e padrões de resposta para um novo caso de Emergência...</p>
              <div className="mt-8 flex gap-2">
                <div className="w-2 h-2 rounded-full bg-primary animate-bounce"></div>
                <div className="w-2 h-2 rounded-full bg-primary animate-bounce [animation-delay:0.2s]"></div>
                <div className="w-2 h-2 rounded-full bg-primary animate-bounce [animation-delay:0.4s]"></div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* How It Works Section */}
      <section className="py-32 bg-surface-container-low relative" id="how-it-works">
        <div className="max-w-[1440px] mx-auto px-8">
          <div className="text-center mb-24 space-y-4">
            <h2 className="font-headline text-4xl font-bold">O Poder da Geração Dinâmica</h2>
            <p className="text-on-surface-variant text-lg">Nossa IA cria desafios únicos para garantir que você nunca decore respostas.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-12">
            <div className="relative p-8 rounded-2xl surface-container-highest transition-transform hover:-translate-y-2 group reveal">
              <div className="absolute -top-6 left-8 w-12 h-12 bg-primary text-on-primary-container flex items-center justify-center rounded-xl font-headline font-black text-2xl shadow-lg">1</div>
              <div className="pt-8 space-y-4">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                  <span className="material-symbols-outlined text-primary text-4xl" style={{ fontVariationSettings: "'FILL' 1" }}>hub</span>
                </div>
                <h3 className="font-headline text-2xl font-bold">Personalização</h3>
                <p className="text-on-surface-variant leading-relaxed">Escolha a especialidade ou deixe a IA decidir. A plataforma gera um paciente com perfil, sintomas e história social totalmente novos a cada clique.</p>
              </div>
            </div>
            <div className="relative p-8 rounded-2xl surface-container-highest transition-transform hover:-translate-y-2 reveal">
              <div className="absolute -top-6 left-8 w-12 h-12 bg-primary text-on-primary-container flex items-center justify-center rounded-xl font-headline font-black text-2xl shadow-lg">2</div>
              <div className="pt-8 space-y-4">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                  <span className="material-symbols-outlined text-primary text-4xl" style={{ fontVariationSettings: "'FILL' 1" }}>dynamic_form</span>
                </div>
                <h3 className="font-headline text-2xl font-bold">Interação Fluida</h3>
                <p className="text-on-surface-variant leading-relaxed">O paciente reage em tempo real. Cada pergunta que você faz altera a percepção da IA, criando um diálogo orgânico e não linear.</p>
              </div>
            </div>
            <div className="relative p-8 rounded-2xl surface-container-highest transition-transform hover:-translate-y-2 reveal">
              <div className="absolute -top-6 left-8 w-12 h-12 bg-primary text-on-primary-container flex items-center justify-center rounded-xl font-headline font-black text-2xl shadow-lg">3</div>
              <div className="pt-8 space-y-4">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-6">
                  <span className="material-symbols-outlined text-primary text-4xl" style={{ fontVariationSettings: "'FILL' 1" }}>analytics</span>
                </div>
                <h3 className="font-headline text-2xl font-bold">Feedback Único</h3>
                <p className="text-on-surface-variant leading-relaxed">Não existem respostas prontas. A IA analisa seu raciocínio clínico específico para aquele caso gerado e oferece mentoria personalizada.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Bento Grid */}
      <section className="py-32" id="features">
        <div className="max-w-[1440px] mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 bg-surface-container rounded-3xl p-12 flex flex-col md:flex-row items-center gap-12 overflow-hidden relative reveal">
              <div className="relative z-10 space-y-6 md:w-1/2">
                <div className="w-12 h-12 bg-secondary-container rounded-full flex items-center justify-center">
                  <span className="material-symbols-outlined text-on-secondary-container" style={{ fontVariationSettings: "'FILL' 1" }}>neurology</span>
                </div>
                <h3 className="font-headline text-4xl font-bold">IA Além do Script</h3>
                <p className="text-on-surface-variant text-lg">Enquanto outras plataformas usam casos pré-escritos, o MedPuzzle utiliza modelos de linguagem médica avançados para sintetizar patologias complexas na hora.</p>
              </div>
              <div className="md:w-1/2 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-surface-container to-transparent z-10"></div>
              </div>
            </div>
            <div className="bg-gradient-to-br from-primary-container/20 to-surface-container-high rounded-3xl p-10 flex flex-col justify-between reveal">
              <div className="space-y-6">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                  <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>model_training</span>
                </div>
                <h3 className="font-headline text-2xl font-bold">Aprendizado Infinito</h3>
                <p className="text-on-surface-variant">Nunca repita o mesmo caso. Cada sessão é uma oportunidade de enfrentar um desafio clínico inédito e genuíno.</p>
              </div>
            </div>
            <div className="md:col-span-2 bg-surface-container-highest rounded-3xl p-12 overflow-hidden group reveal">
              <div className="flex flex-col md:flex-row items-center justify-between gap-12">
                <div className="md:w-1/2 space-y-4">
                  <h3 className="font-headline text-3xl font-bold">Motor de Variação de Sintomas</h3>
                  <p className="text-on-surface-variant">A IA simula apresentações atípicas e comorbidades raras, preparando você para o mundo real, onde os pacientes não seguem o livro didático.</p>
                  <div className="flex flex-wrap gap-2 pt-4">
                    <span className="px-3 py-1 bg-surface-container rounded-full text-xs font-label">Apresentações Atípicas</span>
                    <span className="px-3 py-1 bg-surface-container rounded-full text-xs font-label">Comorbidades Cruzadas</span>
                    <span className="px-3 py-1 bg-surface-container rounded-full text-xs font-label">Laboratórios Randômicos</span>
                  </div>
                </div>
                <div className="md:w-1/2 bg-surface-container-low p-6 rounded-2xl transform group-hover:scale-105 transition-transform border border-primary/20">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <span className="w-2 h-2 rounded-full bg-secondary"></span>
                      <span className="text-sm text-on-surface">Pneumonia Adquirida</span>
                      <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-secondary/20 text-secondary font-label font-semibold">Comum</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-2 h-2 rounded-full bg-primary"></span>
                      <span className="text-sm text-on-surface">Doença de Wilson</span>
                      <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-error/20 text-error font-label font-semibold">Rara</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-2 h-2 rounded-full bg-primary"></span>
                      <span className="text-sm text-on-surface">Hipotireoidismo</span>
                      <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-primary/20 text-primary font-label font-semibold">Moderada</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-2 h-2 rounded-full bg-error"></span>
                      <span className="text-sm text-on-surface">Feocromocitoma</span>
                      <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-error/20 text-error font-label font-semibold">Rara</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="w-2 h-2 rounded-full bg-secondary"></span>
                      <span className="text-sm text-on-surface">Insuficiência Cardíaca</span>
                      <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-secondary/20 text-secondary font-label font-semibold">Comum</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dynamic Generation Promo Section */}
      <section className="py-32 bg-surface-container-lowest overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-8 relative">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div className="space-y-8">
              <h2 className="font-headline text-4xl font-bold leading-tight">Por que MedPuzzle é Diferente?</h2>
              <div className="space-y-6">
                <div className="flex gap-4 reveal">
                  <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <span className="material-symbols-outlined text-primary">cyclone</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Geração Não-Determinística</h4>
                    <p className="text-on-surface-variant">Cada vez que você gera um caso de Pneumonia, o paciente terá uma idade, um histórico de tabagismo e uma queixa principal diferente.</p>
                  </div>
                </div>
                <div className="flex gap-4 reveal">
                  <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <span className="material-symbols-outlined text-primary">psychology_alt</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Raciocínio Clínico Real</h4>
                    <p className="text-on-surface-variant">A IA não apenas responde "sim" ou "não". Ela simula a incerteza do paciente e a progressão da doença durante o atendimento.</p>
                  </div>
                </div>
                <div className="flex gap-4 reveal">
                  <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <span className="material-symbols-outlined text-primary">auto_graph</span>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Escalabilidade de Dificuldade</h4>
                    <p className="text-on-surface-variant">O sistema ajusta a complexidade dos casos conforme você evolui, inserindo distraidores e resultados de exames limítrofes.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative reveal">
              <div className="absolute inset-0 bg-primary/5 blur-3xl rounded-full"></div>
              <div className="relative glass-card border border-outline-variant/20 p-8 rounded-3xl space-y-6">
                <div className="flex justify-between items-center border-b border-outline-variant/20 pb-4">
                  <span className="text-primary font-bold">NOVO CASO GERADO</span>
                  <span className="text-xs bg-surface-container-highest px-2 py-1 rounded">ID: #AI-9921</span>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="material-symbols-outlined text-primary text-sm">person</span>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-on-surface">Maria Silva, 58 anos</p>
                      <p className="text-xs text-on-surface-variant">Cardiologia · Dificuldade Alta</p>
                    </div>
                  </div>
                  <div className="p-4 bg-surface-container-high/50 rounded-xl space-y-3">
                    <p className="text-xs font-label font-bold text-primary uppercase tracking-wider">Hemograma Completo</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                      <span className="text-on-surface-variant">Hemoglobina</span><span className="text-error font-semibold">10.2 g/dL ↓</span>
                      <span className="text-on-surface-variant">Leucócitos</span><span className="text-on-surface">7.800 /mm³</span>
                      <span className="text-on-surface-variant">Plaquetas</span><span className="text-on-surface">210.000 /mm³</span>
                      <span className="text-on-surface-variant">VCM</span><span className="text-error font-semibold">72 fL ↓</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <span className="px-3 py-1 bg-primary/20 rounded-full text-xs font-label text-primary border border-primary/30">Solicitar ECG</span>
                    <span className="px-3 py-1 bg-surface-variant rounded-full text-xs font-label text-on-surface-variant">Histórico</span>
                  </div>
                </div>
                <div className="pt-4 border-t border-outline-variant/20 flex items-center gap-2 text-primary">
                  <span className="material-symbols-outlined text-sm">cached</span>
                  <span className="text-xs font-label uppercase font-bold tracking-widest">IA gerando laboratórios dinâmicos...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-32 relative" id="pricing">
        <div className="max-w-[1440px] mx-auto px-8">
          <div className="text-center mb-20 space-y-4">
            <h2 className="font-headline text-4xl font-bold">Invista na sua Excelência</h2>
            <p className="text-on-surface-variant">Planos desenvolvidos para o futuro da educação médica dinâmica.</p>
          </div>
          {/* Launch banner */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-surface-container-highest border border-primary/20 text-sm">
              <span className="material-symbols-outlined text-primary text-base">star</span>
              <span className="text-on-surface-variant">Desconto de Lançamento — Seja um dos primeiros e garanta <strong className="text-on-surface">o desconto para sempre</strong> enquanto durar!</span>
            </div>
          </div>
          <div className="grid md:grid-cols-4 gap-6 items-end">
            {/* Free Plan */}
            <div className="bg-surface-container-low p-8 rounded-xl flex flex-col h-full border border-outline-variant/10 reveal">
              <div className="mb-8">
                <span className="font-label text-xs uppercase tracking-widest text-primary-container font-bold">Padrão</span>
                <h3 className="font-headline text-2xl font-bold mt-2">Grátis</h3>
                <p className="text-on-surface-variant text-sm mt-2 font-body">Experimente sem compromisso</p>
              </div>
              <div className="mb-10 flex items-baseline">
                <span className="font-headline text-4xl font-extrabold">R$0</span>
                <span className="font-label text-on-surface-variant ml-1">/sempre</span>
              </div>
              <ul className="space-y-4 mb-10 flex-grow">
                <li className="flex items-center gap-3 text-sm"><span className="material-symbols-outlined text-primary text-lg">check_circle</span>1 caso gratuito</li>
              </ul>
              <button className="w-full py-3 rounded-full font-bold font-headline bg-surface-container-high text-on-surface hover:bg-surface-bright transition-colors" onClick={() => onSelectPlan(PLANS[0])}>Começar grátis</button>
            </div>
            {/* Basic Plan */}
            <div className="bg-surface-container-low p-8 rounded-xl flex flex-col h-full border border-outline-variant/10 reveal">
              <div className="mb-8">
                <span className="font-label text-xs uppercase tracking-widest text-primary-container font-bold">Recomendado</span>
                <h3 className="font-headline text-2xl font-bold mt-2">Básico</h3>
                <p className="text-on-surface-variant text-sm mt-2 font-body">Comece com casos básicos</p>
              </div>
              <div className="mb-10 flex items-baseline gap-2">
                <span className="font-headline text-sm line-through text-on-surface-variant">R$15,90</span>
                <span className="font-headline text-4xl font-extrabold">R$9,90</span>
                <span className="font-label text-on-surface-variant ml-1">/mês</span>
              </div>
              <ul className="space-y-4 mb-10 flex-grow">
                <li className="flex items-center gap-3 text-sm"><span className="material-symbols-outlined text-primary text-lg">check_circle</span>20 casos por mês</li>
              </ul>
              <button className="w-full py-3 rounded-full font-bold font-headline bg-surface-container-high text-on-surface hover:bg-surface-bright transition-colors" onClick={() => onSelectPlan(PLANS[1])}>Começar</button>
            </div>
            {/* Pro Plan (Featured) */}
            <div className="relative bg-surface-bright p-8 rounded-xl flex flex-col h-full border-2 border-primary/40 lg:-mb-4 lg:py-12 transform lg:scale-105 z-10 reveal" style={{ boxShadow: '0px 24px 48px -12px rgba(0, 52, 77, 0.4)' }}>
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-primary to-primary-container text-on-primary-container text-[10px] font-extrabold uppercase tracking-[0.2em] py-1 px-4 rounded-full">Mais Popular</div>
              <div className="mb-8">
                <span className="font-label text-xs uppercase tracking-widest text-primary font-bold">Profissional</span>
                <h3 className="font-headline text-2xl font-bold mt-2">Pro</h3>
                <p className="text-on-surface-variant text-sm mt-2 font-body">Desbloqueie a experiência clínica completa</p>
              </div>
              <div className="mb-10 flex items-baseline gap-2">
                <span className="font-headline text-sm line-through text-on-surface-variant">R$49,90</span>
                <span className="font-headline text-4xl font-extrabold">R$29,90</span>
                <span className="font-label text-on-surface-variant ml-1">/mês</span>
              </div>
              <ul className="space-y-4 mb-10 flex-grow">
                <li className="flex items-center gap-3 text-sm"><span className="material-symbols-outlined text-primary text-lg">verified</span>80 casos por mês</li>
              </ul>
              <button className="w-full py-4 rounded-full font-bold font-headline bg-gradient-to-br from-primary to-primary-container text-on-primary-container hover:shadow-[0_0_20px_rgba(137,206,255,0.4)] transition-all" onClick={() => onSelectPlan(PLANS[2])}>Começar Pro</button>
            </div>
            {/* Intensivo Plan */}
            <div className="bg-surface-container-low p-8 rounded-xl flex flex-col h-full border border-outline-variant/10 reveal">
              <div className="mb-8">
                <span className="font-label text-xs uppercase tracking-widest text-secondary font-bold">Elite</span>
                <h3 className="font-headline text-2xl font-bold mt-2">Intensivo</h3>
                <p className="text-on-surface-variant text-sm mt-2 font-body">Para residentes e grupos de estudo</p>
              </div>
              <div className="mb-10 flex items-baseline gap-2">
                <span className="font-headline text-sm line-through text-on-surface-variant">R$79,90</span>
                <span className="font-headline text-4xl font-extrabold">R$59,90</span>
                <span className="font-label text-on-surface-variant ml-1">/mês</span>
              </div>
              <ul className="space-y-4 mb-10 flex-grow">
                <li className="flex items-center gap-3 text-sm"><span className="material-symbols-outlined text-secondary text-lg">star</span>150 casos por mês</li>
              </ul>
              <button className="w-full py-3 rounded-full font-bold font-headline bg-surface-container-high text-on-surface hover:bg-surface-bright transition-colors" onClick={() => onSelectPlan(PLANS[3])}>Começar Intensivo</button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 px-8">
        <div className="max-w-[1440px] mx-auto bg-gradient-to-br from-[#0e2a47] to-[#0b1326] rounded-[3rem] p-16 md:p-24 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 blur-[120px] rounded-full"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-secondary/10 blur-[120px] rounded-full"></div>
          <div className="relative z-10 max-w-3xl mx-auto space-y-10">
            <h2 className="font-headline text-5xl md:text-6xl font-extrabold leading-tight">Pronto para Encarar o Inesperado?</h2>
            <p className="text-xl text-on-surface-variant">Pare de estudar casos prontos. Comece a praticar com uma IA que gera desafios reais e dinâmicos. Seu primeiro caso único está a um clique.</p>
            <div className="flex flex-col md:flex-row justify-center gap-6">
              <button className="bg-gradient-to-br from-primary to-primary-container text-on-primary-container px-12 py-5 rounded-full font-headline font-bold text-xl hover:opacity-90 active:scale-95 transition-all shadow-[0_20px_40px_rgba(0,0,0,0.3)]" onClick={() => onSelectPlan(PLANS[0])}>Gerar Meu Caso Grátis</button>
            </div>
            <p className="text-sm text-on-surface-variant/60 font-label">Experimente a tecnologia que está mudando o ensino médico.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0b1326] w-full py-12 px-8 border-t border-sky-500/10">
        <div className="max-w-7xl mx-auto font-['Manrope'] text-center space-y-4">
          <div className="text-lg font-bold text-slate-200">MedPuzzle</div>
          <p className="text-sm text-slate-400 leading-relaxed max-w-xl mx-auto">A primeira plataforma de simulação diagnóstica puramente generativa. Criando o futuro da prática clínica através de IA.</p>
          <p className="text-sm text-slate-400">Suporte: <a href="mailto:medpuzzlesuporte@gmail.com" className="text-sky-400 hover:underline">medpuzzlesuporte@gmail.com</a></p>
          <p className="text-xs text-slate-500 pt-4 border-t border-sky-500/10">&copy; 2026 MedPuzzle. Não substitui orientação médica profissional.</p>
        </div>
      </footer>
    </div>
  );
}
